import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import { exportToExcel } from "../utils/exportData";
import { BASE_URL } from "../api";
import { getApiHeaders, formatDateDDMMYYYY } from "../utils/helpers";
import axios from "axios";
import CommonTable from "../component/CommonTable";
import ExportButton from "../component/ExportButton";
import MobileFilter from "../component/MobileFilter";
import MessageToster from "../component/MessageToster";

export default function BonusHistory() {
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Claim Modal State
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState("");

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  const today = new Date().toISOString().split("T")[0];

  const fetchBonusHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/deposit/bonus_history_list`, {
        headers: getApiHeaders(),
      });

      const raw =
        res.data?.data ??
        res.data?.bonus_history_list ??
        res.data?.bonus_list ??
        res.data?.history ??
        (Array.isArray(res.data) ? res.data : []);

      let list = [];
      if (Array.isArray(raw)) {
        list = Array.isArray(raw[0]) ? raw.flat(Infinity) : raw;
      } else if (raw && typeof raw === "object") {
        list = [raw];
      }

      const validRecords = list.filter((item) => {
        if (!item || typeof item !== "object") return false;
        const keys = Object.keys(item);
        const nonMsgKeys = keys.filter(
          (k) => !["msg", "msgcode", "Msg", "MsgCode", "message"].includes(k)
        );
        return nonMsgKeys.length > 0;
      });

      setData(validRecords);
    } catch (error) {
      console.error("Error fetching bonus history:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBonusHistory();
  }, [fetchBonusHistory]);

  const getItemDateString = (item) => {
    const dateVal = item.ondate || item.created_at || item.date || item.datetime;
    if (!dateVal) return "";
    if (typeof dateVal === "string") {
      if (dateVal.includes("T")) return dateVal.split("T")[0];
      if (dateVal.includes(" ")) return dateVal.split(" ")[0];
    }
    const d = new Date(dateVal);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  const filteredData = data.filter((item) => {
    let match = true;

    if (appliedFromDate || appliedToDate) {
      const itemDateStr = getItemDateString(item);
      if (itemDateStr) {
        const itemDate = new Date(itemDateStr);
        itemDate.setHours(0, 0, 0, 0);

        if (appliedFromDate) {
          const from = new Date(appliedFromDate);
          from.setHours(0, 0, 0, 0);
          if (itemDate < from) match = false;
        }

        if (appliedToDate) {
          const to = new Date(appliedToDate);
          to.setHours(23, 59, 59, 999);
          if (itemDate > to) match = false;
        }
      }
    }

    if (!match) return false;

    if (appliedSearchTerm) {
      if (!item || typeof item !== "object") return false;
      match = Object.values(item).some((val) =>
        String(val ?? "")
          .toLowerCase()
          .includes(appliedSearchTerm.toLowerCase())
      );
    }

    return match;
  });

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setAppliedSearchTerm("");
    setAppliedFromDate("");
    setAppliedToDate("");
  };

  const handleOpenClaimModal = (row) => {
    setSelectedBonus(row);
    setClaimError("");
    setIsClaimModalOpen(true);
  };

  const handleClaimSubmit = async () => {
    if (!selectedBonus) return;

    setClaimLoading(true);
    setClaimError("");
    try {
      const payload = {
        id: selectedBonus.id,
      };

      const res = await axios.post(`${BASE_URL}/deposit/bonus_claim`, payload, {
        headers: getApiHeaders(),
      });

      if (
        (res.data?.code === 200 || res.data?.status === "success") &&
        res.data?.code !== 400 &&
        res.data?.status !== "fail" &&
        res.data?.status !== "error"
      ) {
        MessageToster(
          "success",
          res.data?.message || res.data?.msg || "Bonus claimed successfully!"
        );
        setClaimError("");
        setIsClaimModalOpen(false);
        fetchBonusHistory();
      } else {
        const errMsg =
          res.data?.message ||
          res.data?.msg ||
          "Error while claiming bonus. Please try again later.";
        setClaimError(errMsg);
      }
    } catch (error) {
      console.error("Error claiming bonus:", error);
      const errMsg =
        error.response?.data?.message ||
        error.response?.data?.msg ||
        "Something went wrong. Please try again later.";
      setClaimError(errMsg);
    } finally {
      setClaimLoading(false);
    }
  };

  const handleExport = () => {
    const exportData = filteredData.map((item, index) => ({
      "#": index + 1,
      Date: formatDateDDMMYYYY(item.ondate || item.created_at || item.date) || "-",
      "Invoice ID": item.invoiceid || item.id || "-",
      "Deposit Amount": item.deposit_amount || item.amount || 0,
      "Bonus Amount": item.bonus_amount || item.bonus || 0,
      "Bonus %": item.bonus_percent || 0,
      "Lots Required": item.lots_required || item.required_lots || 0,
      "Lots Completed": item.lots_completed || item.completed_lots || 0,
      "Claimed Amount": item.claimed_amount || 0,
      "Remaining Amount": item.remaining_amount || 0,
      Status: item.status || item.istatus || "-",
    }));
    exportToExcel(exportData, "Bonus_History", "default");
  };

  const columns = [
    {
      header: "Date",
      render: (row) => (
        <span>
          {formatDateDDMMYYYY(row.ondate || row.created_at || row.date) || "-"}
        </span>
      ),
    },
    {
      header: "Invoice ID",
      render: (row) => (
        <span className="font-monospace font-size-12 fw-semibold">
          {row.invoiceid || row.id || "-"}
        </span>
      ),
    },
    {
      header: "Deposit Amount",
      render: (row) => {
        const amt = row.deposit_amount ?? row.amount;
        return (
          <span className="fw-semibold">
            {amt != null ? `$${parseFloat(amt).toFixed(2)}` : "-"}
          </span>
        );
      },
    },
    {
      header: "Bonus Amount",
      render: (row) => {
        const bonus = row.bonus_amount ?? row.bonus;
        return (
          <div className="d-flex align-items-center gap-1">
            <span className="fw-bold" style={{ color: "var(--bs-primary)" }}>
              {bonus != null ? `$${parseFloat(bonus).toFixed(2)}` : "-"}
            </span>
            {row.bonus_percent ? (
              <span className="badge bg-primary-subtle text-primary font-size-11">
                {row.bonus_percent}%
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      header: "Lots (Done / Req)",
      render: (row) => {
        const comp = row.lots_completed ?? 0;
        const req = row.lots_required ?? 0;
        const pct = req > 0 ? Math.min(100, Math.round((comp / req) * 100)) : 0;
        return (
          <div style={{ minWidth: "120px" }}>
            <div className="d-flex justify-content-between font-size-11 mb-1">
              <span className="fw-medium">{comp} L</span>
              <span className="text-muted">{req} L</span>
            </div>
            <div className="progress" style={{ height: "5px" }}>
              <div
                className={`progress-bar ${pct >= 100 ? "bg-success" : "bg-primary"}`}
                role="progressbar"
                style={{ width: `${pct}%` }}
              ></div>
            </div>
          </div>
        );
      },
    },
    {
      header: "Claimed",
      render: (row) => {
        const claimed = row.claimed_amount;
        return (
          <span className="fw-semibold text-success">
            {claimed != null ? `$${parseFloat(claimed).toFixed(2)}` : "$0.00"}
          </span>
        );
      },
    },
    {
      header: "Remaining",
      render: (row) => {
        const rem = row.remaining_amount;
        return (
          <span
            className="fw-bold"
            style={{
              color:
                Number(rem) > 0
                  ? "var(--bs-warning)"
                  : "var(--bs-secondary-color)",
            }}
          >
            {rem != null ? `$${parseFloat(rem).toFixed(2)}` : "$0.00"}
          </span>
        );
      },
    },
    {
      header: "Status",
      render: (row) => {
        const rawStatus = row.status || row.istatus || "Pending";
        const statusStr = String(rawStatus).toLowerCase();
        const isSuccess = [
          "completed",
          "approved",
          "done",
          "credited",
          "claimed",
        ].includes(statusStr);
        const isWarning = ["pending", "0", "review"].includes(statusStr);

        return (
          <span
            className={`badge fw-bold px-3 py-2 rounded-pill ${isSuccess
              ? "bg-success bg-opacity-10 text-success"
              : isWarning
                ? "bg-warning bg-opacity-10 text-warning"
                : "bg-danger bg-opacity-10 text-danger"
              }`}
          >
            {rawStatus}
          </span>
        );
      },
    },
    {
      header: "Action",
      className: "text-center",
      headerClassName: "text-center",
      render: (row) => {
        const rawStatus = String(row.status || "").toLowerCase();
        const remaining = Number(row.remaining_amount);
        const isCompleted =
          rawStatus === "completed" ||
          (row.remaining_amount != null && remaining <= 0);

        if (isCompleted) {
          return (
            <button
              type="button"
              className="btn btn-sm btn-light border text-muted px-2 py-1 font-size-12 d-inline-flex align-items-center gap-1"
              disabled
              title="All bonus claimed"
            >
              <i className="bx bx-check-double text-success"></i> Claimed
            </button>
          );
        }

        return (
          <button
            type="button"
            className="btn btn-sm btn-primary waves-effect waves-light px-3 py-1 font-size-12 d-inline-flex align-items-center gap-1 shadow-sm"
            onClick={() => handleOpenClaimModal(row)}
          >
            <i className="bx bx-gift"></i> Claim
          </button>
        );
      },
    },
  ];

  return (
    <div id="layout-wrapper">
      <Topbar />
      <Sidebar />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid mb-4">
            {/* Standard Page Title & Buttons matching other pages */}
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div>
                      <h2 className="fw-700 mb-0">Bonus History</h2>
                      <p
                        className="mb-0 font-size-13"
                        style={{ color: "var(--bs-secondary-color)" }}
                      >
                        View your claimed bonuses, required trading lots, and progress
                      </p>
                    </div>
                  </div>

                  <div className="page-title-right d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                      onClick={() => navigate("/claim_history")}
                    >
                      <i className="bx bx-receipt me-2 fs-5"></i> Claim History
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                      onClick={() => navigate("/bonus-programs")}
                    >
                      <i className="bx bx-gift me-2 fs-5"></i> Bonus Programs
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Table Card */}
            <div className="row">
              <div className="col-12">
                <div className="card shadow-sm border-0" style={{ borderRadius: "12px" }}>
                  <div className="card-body">
                    {/* Desktop Filters */}
                    <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                      <div>
                        <label htmlFor="fromDate">From Date</label>
                        <input
                          className="form-control"
                          type="date"
                          name="fromDate"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          max={toDate || today}
                          id="fromDate"
                        />
                      </div>
                      <div>
                        <label htmlFor="toDate">To Date</label>
                        <input
                          className="form-control"
                          type="date"
                          name="toDate"
                          min={fromDate || undefined}
                          max={today}
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          id="toDate"
                        />
                      </div>
                      <div>
                        <label htmlFor="search">Search</label>
                        <input
                          className="form-control"
                          type="text"
                          id="search"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-primary waves-effect waves-light"
                          onClick={handleSearch}
                        >
                          Search
                        </button>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-light border waves-effect waves-light"
                          onClick={clearFilters}
                          disabled={
                            !fromDate &&
                            !toDate &&
                            !searchTerm &&
                            !appliedFromDate &&
                            !appliedToDate &&
                            !appliedSearchTerm
                          }
                        >
                          Clear
                        </button>
                      </div>
                      <div className="ms-auto">
                        <ExportButton
                          disabled={!filteredData || filteredData.length === 0}
                          onClick={handleExport}
                        />
                      </div>
                    </div>

                    {/* Mobile Filters */}
                    <MobileFilter
                      title="Bonus History"
                      onExport={handleExport}
                      exportDisabled={!filteredData || filteredData.length === 0}
                    >
                      <div>
                        <label htmlFor="fromDateMobile">From Date</label>
                        <input
                          className="form-control"
                          type="date"
                          name="fromDate"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          max={toDate || today}
                          id="fromDateMobile"
                        />
                      </div>
                      <div>
                        <label htmlFor="toDateMobile">To Date</label>
                        <input
                          className="form-control"
                          type="date"
                          name="toDate"
                          min={fromDate || undefined}
                          max={today}
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          id="toDateMobile"
                        />
                      </div>
                      <div>
                        <label htmlFor="searchMobile">Search</label>
                        <input
                          className="form-control"
                          type="text"
                          id="searchMobile"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-primary waves-effect waves-light flex-grow-1"
                          onClick={handleSearch}
                          data-bs-dismiss="offcanvas"
                          data-bs-target="#filterOffcanvasMobile"
                        >
                          Search
                        </button>
                        <button
                          type="button"
                          className="btn btn-light waves-effect waves-light border flex-grow-1"
                          onClick={clearFilters}
                          disabled={
                            !fromDate &&
                            !toDate &&
                            !searchTerm &&
                            !appliedFromDate &&
                            !appliedToDate &&
                            !appliedSearchTerm
                          }
                          data-bs-dismiss="offcanvas"
                          data-bs-target="#filterOffcanvasMobile"
                        >
                          Clear
                        </button>
                      </div>
                    </MobileFilter>

                    {/* Standard CommonTable */}
                    <CommonTable
                      key={`${appliedFromDate}_${appliedToDate}`}
                      columns={columns}
                      data={filteredData}
                      loading={loading}
                      loadingMessage={
                        <div>
                          <div
                            className="spinner-border text-primary spinner-border-sm me-2"
                            role="status"
                          ></div>
                          <span>Loading bonus history...</span>
                        </div>
                      }
                      noDataMessage="No bonus history records found"
                      tableClassName="table table-hover align-middle table-nowrap mb-0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>

      {/* Claim Bonus Modal */}
      {isClaimModalOpen && selectedBonus && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.55)",
            backdropFilter: "blur(4px)",
            zIndex: 1060,
          }}
          role="dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget && !claimLoading) {
              setIsClaimModalOpen(false);
            }
          }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div
              className="modal-content border-0 shadow-lg"
              style={{
                borderRadius: "16px",
                backgroundColor: "#ffffff",
                color: "#212529",
                overflow: "hidden",
              }}
            >
              {/* Modal Header */}
              <div
                className="modal-header border-bottom py-3 px-4 d-flex align-items-center justify-content-between"
                style={{ borderColor: "#edf2f7", backgroundColor: "#ffffff" }}
              >
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: "rgba(85, 110, 230, 0.12)",
                      color: "#556ee6",
                      width: "40px",
                      height: "40px",
                    }}
                  >
                    <i className="bx bx-gift fs-4"></i>
                  </div>
                  <div>
                    <h5
                      className="modal-title fw-bold mb-0"
                      style={{ color: "#212529" }}
                    >
                      Claim Bonus
                    </h5>
                    <span className="font-size-12 text-muted">
                      Invoice:{" "}
                      <span className="font-monospace fw-semibold text-primary">
                        {selectedBonus.invoiceid || selectedBonus.id}
                      </span>
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  disabled={claimLoading}
                  onClick={() => setIsClaimModalOpen(false)}
                ></button>
              </div>

              {/* Modal Details */}
              <div
                className="modal-body p-4"
                style={{ backgroundColor: "#ffffff" }}
              >
                {claimError && (
                  <div
                    className="alert alert-danger d-flex align-items-center gap-2 mb-3 py-2 px-3 font-size-13 shadow-sm"
                    role="alert"
                  >
                    <i className="bx bx-error-circle fs-5 flex-shrink-0"></i>
                    <div>{claimError}</div>
                  </div>
                )}

                {/* Bonus Summary Card */}
                <div
                  className="p-3 rounded-3 mb-3"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #edf2f7",
                  }}
                >
                  <div className="row g-2 text-center font-size-12">
                    <div
                      className="col-4 border-end"
                      style={{ borderColor: "#e2e8f0" }}
                    >
                      <span className="text-muted d-block font-size-11 text-uppercase">
                        Deposit
                      </span>
                      <span className="fw-bold font-size-14 text-dark">
                        $
                        {parseFloat(
                          selectedBonus.deposit_amount ?? selectedBonus.amount ?? 0
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div
                      className="col-4 border-end"
                      style={{ borderColor: "#e2e8f0" }}
                    >
                      <span className="text-muted d-block font-size-11 text-uppercase">
                        Total Bonus
                      </span>
                      <span className="fw-bold font-size-14 text-primary">
                        $
                        {parseFloat(
                          selectedBonus.bonus_amount ?? selectedBonus.bonus ?? 0
                        ).toFixed(2)}
                      </span>
                    </div>
                    <div className="col-4">
                      <span className="text-muted d-block font-size-11 text-uppercase">
                        Available
                      </span>
                      <span className="fw-bold font-size-14 text-success">
                        $
                        {parseFloat(
                          selectedBonus.remaining_amount != null
                            ? selectedBonus.remaining_amount
                            : selectedBonus.bonus_amount ?? selectedBonus.bonus ?? 0
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div
                    className="mt-3 pt-2 border-top"
                    style={{ borderColor: "#e2e8f0" }}
                  >
                    <div className="d-flex justify-content-between font-size-11 mb-1">
                      <span className="text-muted">Lots Progress</span>
                      <span className="fw-semibold text-dark">
                        {selectedBonus.lots_completed || 0} /{" "}
                        {selectedBonus.lots_required || 0} Lots
                      </span>
                    </div>
                    <div className="progress" style={{ height: "6px" }}>
                      <div
                        className="progress-bar bg-primary"
                        role="progressbar"
                        style={{
                          width: `${Math.min(
                            100,
                            Math.round(
                              ((selectedBonus.lots_completed || 0) /
                                (selectedBonus.lots_required || 1)) *
                              100
                            )
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Additional Details Table-Style List */}
                <div
                  className="rounded-3 p-3 border mb-3"
                  style={{
                    backgroundColor: "#ffffff",
                    borderColor: "#edf2f7",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom font-size-13">
                    <span className="text-muted">Date</span>
                    <span className="fw-medium text-dark">
                      {formatDateDDMMYYYY(
                        selectedBonus.ondate ||
                        selectedBonus.created_at ||
                        selectedBonus.date
                      )}
                    </span>
                  </div>
                  {selectedBonus.bonus_percent ? (
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom font-size-13">
                      <span className="text-muted">Bonus Rate</span>
                      <span className="badge bg-primary-subtle text-primary">
                        {selectedBonus.bonus_percent}%
                      </span>
                    </div>
                  ) : null}
                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom font-size-13">
                    <span className="text-muted">Status</span>
                    <span className="badge bg-success-subtle text-success">
                      {selectedBonus.status || selectedBonus.istatus || "Active"}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-2 font-size-13">
                    <span className="text-muted fw-semibold">Claimable Amount</span>
                    <span className="fw-bold text-success font-size-16">
                      $
                      {parseFloat(
                        selectedBonus.remaining_amount != null
                          ? selectedBonus.remaining_amount
                          : selectedBonus.bonus_amount ?? selectedBonus.bonus ?? 0
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="alert alert-info d-flex align-items-center gap-2 mb-0 py-2 px-3 font-size-12">
                  <i className="bx bx-info-circle fs-5 flex-shrink-0"></i>
                  <div>
                    Are you sure you want to claim this bonus? Click <strong>Claim Bonus</strong> to proceed.
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div
                className="modal-footer border-top py-3 px-4 d-flex justify-content-end gap-2"
                style={{
                  borderColor: "#edf2f7",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <button
                  type="button"
                  className="btn btn-light waves-effect border"
                  onClick={() => setIsClaimModalOpen(false)}
                  disabled={claimLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary waves-effect waves-light d-flex align-items-center gap-2"
                  disabled={claimLoading}
                  onClick={handleClaimSubmit}
                >
                  {claimLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span>Claiming...</span>
                    </>
                  ) : (
                    <>
                      <i className="bx bx-check-circle fs-5"></i>
                      <span>Claim Bonus</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
